import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Check, ExternalLink } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/apiClient";

// ReferralWelcomeToast — the COUNTERPART to ReferralThanksToast.
//
// Fires on a newly-referred user's FIRST authenticated page load:
// "🤝 @{referrer} invited you here. They're already following you —
//  follow them back?"
//
// The "they're already following you" claim is made true at register
// time by auth.py auto-creating a Follow row from referrer→new-user.
// So this toast is genuinely warm, not aspirational.
//
// Storage gate: `globaleyes_welcome_toast_seen_v1` is a per-username
// `{username: timestamp}` map. We persist on RENDER (not on click) so
// users who close the tab without acting never re-see it — same
// "don't re-pester" policy as the share toast.
//
// Three states like the Thanks toast: idle / working / followed / failed.
// "Already following" 400 is treated as a successful state (mutual
// connection is the goal regardless of who clicked first).
const STORAGE_KEY = "globaleyes_welcome_toast_seen_v1";

const WelcomeToastBody = ({ referrerHandle, onView, dismiss }) => {
  const [state, setState] = useState("idle");
  const doFollow = async () => {
    if (state !== "idle") return;
    setState("working");
    try {
      await api.post(`/users/${referrerHandle}/follow`);
      setState("followed");
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (/already following/i.test(detail)) {
        setState("followed");
      } else {
        setState("failed");
      }
    }
  };
  return (
    <div className="bg-gray-900 border border-amber-500/30 rounded-lg shadow-lg px-4 py-3 min-w-[320px] max-w-md">
      <p className="text-amber-300 font-semibold text-sm">
        🤝 @{referrerHandle} invited you here
      </p>
      <p className="text-gray-400 text-xs mt-0.5">
        They&apos;re already following you. Follow them back to start a mutual connection.
      </p>
      <div className="flex items-center gap-2 mt-2.5">
        <button
          type="button"
          onClick={doFollow}
          disabled={state === "working" || state === "followed"}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
            state === "followed"
              ? "bg-amber-500/20 text-amber-200 border border-amber-500/40 cursor-default"
              : state === "failed"
                ? "bg-rose-500/15 text-rose-300 border border-rose-500/40"
                : "bg-amber-400 hover:bg-amber-300 text-gray-900 disabled:opacity-60"
          }`}
          data-testid="welcome-follow-back-btn"
        >
          {state === "working" && <span className="h-3 w-3 animate-spin border border-current border-t-transparent rounded-full" />}
          {state === "followed" && <Check className="h-3.5 w-3.5" />}
          {(state === "idle" || state === "failed") && <UserPlus className="h-3.5 w-3.5" />}
          {state === "idle" && "Follow back"}
          {state === "working" && "Following…"}
          {state === "followed" && "Following"}
          {state === "failed" && "Retry"}
        </button>
        <button
          type="button"
          onClick={() => { onView(); dismiss(); }}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-gray-700 text-gray-300 hover:bg-gray-800 transition-colors"
          data-testid="welcome-view-profile-btn"
        >
          <ExternalLink className="h-3 w-3" /> View profile
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="ml-auto text-gray-500 hover:text-gray-300 text-[11px]"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
};

const ReferralWelcomeToast = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const firedFor = useRef(null);

  useEffect(() => {
    if (!token || !user?.username || !user?.referred_by_username) return;
    // StrictMode dedupe — same pattern as FirstLoginShareToast.
    if (firedFor.current === user.username) return;

    let seenMap;
    try { seenMap = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); }
    catch { seenMap = {}; }
    if (seenMap[user.username]) return;
    firedFor.current = user.username;

    // 2.5s delay so the post-registration success toast lands first.
    const t = setTimeout(() => {
      const referrer = user.referred_by_username;
      toast.custom((id) => (
        <WelcomeToastBody
          referrerHandle={referrer}
          onView={() => navigate(`/${referrer}`)}
          dismiss={() => toast.dismiss(id)}
        />
      ), { duration: 12000 });
      // Persist on render so a hard reload mid-toast doesn't re-fire.
      try {
        seenMap[user.username] = Date.now();
        localStorage.setItem(STORAGE_KEY, JSON.stringify(seenMap));
      } catch { /* private mode — accept the re-prompt on next session */ }
    }, 2500);
    return () => clearTimeout(t);
  }, [token, user?.username, user?.referred_by_username, navigate]);

  return null;
};

export default ReferralWelcomeToast;
